import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueries, useQuery } from '@tanstack/react-query';

import { getTeam, listTeams, type Team } from '@/api/teams';

// useAsyncTeamSearch is the shared data layer for any team picker / team
// filter UI. The bare listTeams call defaults to limit=25 on the server,
// so the previous "pass me all teams" pattern silently dropped teams
// beyond #25 from every picker — searches over the loaded subset never
// matched the missing teams.
//
// This hook:
//   - debounces the search input (250ms — matches Teams page + TemplateGenerateDialog)
//   - queries the server with the debounced search, paged at 200 (the hard max)
//   - exposes `truncated` so the UI can hint "N more — refine search"
//   - resolves the displayed labels for any teams the caller is already
//     pinning by ID (currently selected in a single-select picker or
//     already-checked items in a multi-select filter), independently
//     of the current search results. Without this, re-opening a picker
//     with a team selected that falls outside the current search would
//     leave the trigger label empty.

export interface TeamLite {
  id: string;
  name: string;
  color: string;
}

interface Options {
  // When false the hook becomes inert (no queries fire). Useful for
  // pickers that only fetch while their popover is open.
  enabled?: boolean;
  // IDs the caller wants display labels for, regardless of current search.
  // Each ID gets a tiny cached getTeam query under
  // ['teams', slug, 'resolve', id], so re-opening a picker with the
  // same selection is instant.
  resolveIds?: string[];
}

export interface AsyncTeamSearch {
  search: string;
  setSearch: (value: string) => void;
  // Top 200 teams matching the debounced search.
  teams: TeamLite[];
  total: number;
  truncated: boolean;
  isLoading: boolean;
  // Labels for resolveIds. Pulled from the current search page when
  // possible, otherwise from per-ID lookup queries.
  resolved: Map<string, TeamLite>;
}

const PAGE_SIZE = 200;
const DEBOUNCE_MS = 250;

function toLite(t: Team): TeamLite {
  return { id: t.id, name: t.name, color: t.color };
}

export function useAsyncTeamSearch(slug: string, options: Options = {}): AsyncTeamSearch {
  const { enabled = true, resolveIds } = options;
  const { user } = useAuth();
  const token = user?.access_token ?? null;

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  const listQuery = useQuery({
    queryKey: ['teams', slug, 'async-search', debounced],
    queryFn: () => listTeams(token, slug, { limit: PAGE_SIZE, search: debounced }),
    enabled: enabled && !!slug && !!token,
  });

  const teams = useMemo<TeamLite[]>(
    () => (listQuery.data?.teams ?? []).map(toLite),
    [listQuery.data],
  );
  const total = listQuery.data?.total ?? 0;

  // Dedupe + sort for stable hook input.
  const idsToResolve = useMemo(() => {
    if (!resolveIds || resolveIds.length === 0) return [] as string[];
    return Array.from(new Set(resolveIds.filter(Boolean))).sort();
  }, [resolveIds]);

  // Skip the lookup for any ID we already have on the current search page —
  // saves the round-trip in the common case.
  const inPageById = useMemo(() => {
    const m = new Map<string, TeamLite>();
    for (const t of teams) m.set(t.id, t);
    return m;
  }, [teams]);
  const stillMissing = useMemo(
    () => idsToResolve.filter((id) => !inPageById.has(id)),
    [idsToResolve, inPageById],
  );

  const lookupQueries = useQueries({
    queries: stillMissing.map((id) => ({
      queryKey: ['teams', slug, 'resolve', id],
      queryFn: () => getTeam(token, slug, id),
      enabled: enabled && !!slug && !!token,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const resolved = useMemo(() => {
    const m = new Map<string, TeamLite>();
    for (const id of idsToResolve) {
      const hit = inPageById.get(id);
      if (hit) m.set(id, hit);
    }
    lookupQueries.forEach((q, i) => {
      const id = stillMissing[i];
      if (id && q.data) m.set(id, toLite(q.data));
    });
    return m;
    // lookupQueries reference changes each render, but its `data` payloads
    // are what actually matter — react-query memoises those, so the
    // resulting Map is stable when no team changed.
  }, [idsToResolve, inPageById, stillMissing, lookupQueries]);

  return {
    search,
    setSearch,
    teams,
    total,
    truncated: total > teams.length,
    isLoading: listQuery.isLoading,
    resolved,
  };
}
